import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { flushSync } from 'react-dom';
import { Rnd } from 'react-rnd';
import { getTaskStatusApi } from '../../service/task';
import Hls from 'hls.js';
import './SubtitleOverlayPlayer.scss';
import { processVideoUrl } from '../../utils/videoUrl';
import {
  buildGenerateVideoRequestPayloadFromStyleState,
  buildSubtitleStyleBackendPayload,
  buildSubtitleStylePersistSnapshot,
  clampSubtitleBoxWidthPx,
  clampSubtitleBoxWidthRatio,
  clampSubtitleAdjustableLineNumber,
  computeFixedSubtitleBoxHeightPx,
  computeFixedSubtitleBoxHeightFromScale,
  computeMinSubtitleBoxWidthPx,
  computePreviewSubtitleDrawFontPx,
  computeSubtitleBoxPadding,
  computeSubtitleCornerLockAspect,
  computeWrappedSubtitleTextBlockHeightPx,
  isSubtitleWrapLayoutValid,
  deriveSubtitleFontScaleFromBoxHeight,
  getContainFitTransform,
  normalizeSubtitleLineNumber,
  SUBTITLE_BOX_WIDTH_RATIO_MAX,
  SUBTITLE_BOX_WIDTH_RATIO_MIN,
  wrapSubtitleTextByTokens,
  SUBTITLE_DEFAULT_LINE_NUMBER,
  SUBTITLE_LINE_NUMBER_ADJUST_MAX,
  SUBTITLE_LINE_NUMBER_MIN,
  SUBTITLE_PREVIEW_BOX_HEIGHT_FRAC,
  SUBTITLE_PREVIEW_BOX_WIDTH_FRAC,
  SUBTITLE_PREVIEW_LINE_HEIGHT_RATIO,
  SUBTITLE_PREVIEW_SCALE_MAX,
  SUBTITLE_PREVIEW_SCALE_MIN,
  SUBTITLE_PREVIEW_STROKE_TO_FONT_FRAC,
} from '../../utils/autoSubtitleGeometry';
import {
  buildPostTextFromPlainAndWrap,
  stripSubtitleLineBreakMarkers,
  stripTargetTextUserLineBreaks,
} from '../../utils/subtitlePostText';
import type { HandleStyles } from 'react-rnd';
import type { ResizeDirection } from 're-resizable';
import type {
  GenerateVideoRequestPayload,
  SubtitleStyleBackendPayload,
} from '../../utils/autoSubtitleGeometry';

export type {
  GenerateVideoRequestPayload,
  SubtitleStyleBackendPayload,
  SubtitleStylePersistSnapshot,
} from '../../utils/autoSubtitleGeometry';

/**
 * 修改 canvas.width/height 会重置 2D 上下文全部属性；在每次正式绘制前调用，避免沿用默认 font/alpha/transform。
 */
function primeSubtitleCanvas2DContext(c: CanvasRenderingContext2D) {
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.globalAlpha = 1;
  c.globalCompositeOperation = 'source-over';
  try {
    c.filter = 'none';
  } catch {
    /* ignore */
  }
  c.imageSmoothingEnabled = true;
  c.font = '16px sans-serif';
  c.fillStyle = '#ffffff';
  c.strokeStyle = '#000000';
  c.textAlign = 'left';
  c.textBaseline = 'alphabetic';
  c.lineWidth = 1;
  c.lineJoin = 'miter';
  c.miterLimit = 10;
}

/** 字幕文字与框上下边不设间距（与 computeSubtitleBoxPadding 的 paddingY 一致） */
const SUBTITLE_VERTICAL_PADDING = 0;

/** 角点：白圆点；左右：白短竖条（四角圆角）— 尺寸尽量小，减少挡画面 */
const SUBTITLE_RND_RESIZE_HANDLE_STYLES: HandleStyles = {
  left: {
    width: 4,
    height: 22,
    top: '50%',
    // 相对 padding 边定位；外层有 1px 白框，-width/2 对齐的是内边，再外移 0.5px 与框线视觉中心重合
    left: -2.5,
    transform: 'translateY(-50%)',
    borderRadius: 2,
    background: '#fff',
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.35)',
    cursor: 'ew-resize',
    zIndex: 2,
  },
  right: {
    width: 4,
    height: 22,
    top: '50%',
    right: -2.5,
    left: 'auto',
    bottom: 'auto',
    transform: 'translateY(-50%)',
    borderRadius: 2,
    background: '#fff',
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.35)',
    cursor: 'ew-resize',
    zIndex: 2,
  },
  topLeft: {
    width: 8,
    height: 8,
    top: -4,
    left: -4,
    right: 'auto',
    bottom: 'auto',
    borderRadius: '50%',
    background: '#fff',
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.35)',
    cursor: 'nwse-resize',
    zIndex: 2,
  },
  topRight: {
    width: 8,
    height: 8,
    top: -4,
    right: -4,
    left: 'auto',
    bottom: 'auto',
    borderRadius: '50%',
    background: '#fff',
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.35)',
    cursor: 'nesw-resize',
    zIndex: 2,
  },
  bottomLeft: {
    width: 8,
    height: 8,
    bottom: -4,
    left: -4,
    top: 'auto',
    right: 'auto',
    borderRadius: '50%',
    background: '#fff',
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.35)',
    cursor: 'nesw-resize',
    zIndex: 2,
  },
  bottomRight: {
    width: 8,
    height: 8,
    bottom: -4,
    right: -4,
    top: 'auto',
    left: 'auto',
    borderRadius: '50%',
    background: '#fff',
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.35)',
    cursor: 'nwse-resize',
    zIndex: 2,
  },
};

function isSubtitleResizeCorner(d: ResizeDirection): boolean {
  return d === 'topLeft' || d === 'topRight' || d === 'bottomLeft' || d === 'bottomRight';
}

function isSubtitleResizeEastWest(d: ResizeDirection): boolean {
  return d === 'left' || d === 'right';
}

type SubtitleWrapFn = (text: string, maxWidth: number, ctx: CanvasRenderingContext2D, maxLines?: number) => string[];

/** 与 renderSubtitleInternal 一致：多行换行后的实际块宽高，供 Rnd 与点击检测（避免仍用单行模板高度） */
function computeSubtitleDrawBlockMetrics(
  ctx: CanvasRenderingContext2D,
  text: string,
  fontScale: number,
  widthRatio: number,
  info: { w: number; h: number },
  wrapTextFn: SubtitleWrapFn,
  intrinsicVideoWidth?: number,
  intrinsicVideoHeight?: number
): { bgW: number; bgH: number } | null {
  const contentW = info.w;
  if (!(contentW > 0) || !(info.h > 0)) return null;
  const trimmed = text.trim();
  if (!trimmed) return null;

  const fontSize = computePreviewSubtitleDrawFontPx({
    scale: fontScale,
    contentW,
    contentH: info.h,
    intrinsicVideoWidth,
    intrinsicVideoHeight,
  });
  const { paddingX } = computeSubtitleBoxPadding(fontSize);
  const maxWidth = contentW * widthRatio - paddingX * 2;

  ctx.font = `${fontSize}px Microsoft YaHei, Arial, sans-serif`;
  const lines = wrapTextFn(trimmed, maxWidth, ctx);
  const { paddingY } = computeSubtitleBoxPadding(fontSize);
  const bgW = contentW * widthRatio;
  const bgH = computeWrappedSubtitleTextBlockHeightPx(fontSize, lines.length, paddingY);
  return { bgW: Math.max(1, bgW), bgH: Math.max(1, bgH) };
}

/** 仅角点 + 左右边：上下边无手柄 */
const SUBTITLE_RND_RESIZE_ENABLE = {
  top: false,
  bottom: false,
  left: true,
  right: true,
  topLeft: true,
  topRight: true,
  bottomLeft: true,
  bottomRight: true,
} as const;

export interface Subtitle {
  start: number;
  end: number;
  text: string;
  /** 台词行 id（同 subtitles[].id）；时间轴片段用 data-subtitle-id 存此值 */
  id?: number;
}

/** 叠加字幕相对 start 提前显示的秒数，减轻观感延迟 */
const SUBTITLE_OVERLAY_DISPLAY_LEAD_SEC = 0.2;

function findActiveSubtitleAtTime(subs: Subtitle[], t: number): Subtitle | undefined {
  for (const s of subs) {
    if (t >= s.start - SUBTITLE_OVERLAY_DISPLAY_LEAD_SEC && t < s.end) return s;
  }
  return undefined;
}

function findActiveSubtitleForOverlay(
  subs: Subtitle[],
  t: number,
  lockLineId?: number | null
): Subtitle | undefined {
  if (lockLineId != null && lockLineId > 0) {
    const locked = subs.find((s) => s.id === lockLineId);
    if (
      locked?.text &&
      t >= locked.start - SUBTITLE_OVERLAY_DISPLAY_LEAD_SEC &&
      t < locked.end
    ) {
      return locked;
    }
  }
  return findActiveSubtitleAtTime(subs, t);
}

// 直接控制接口
export interface SubtitleOverlayPlayerRef {
  seekToTime: (time: number) => void;
  play: () => void;
  pause: () => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  isPlaying: () => boolean;
  /** 视频原始分辨率绝对 px，供提交后端；未就绪时返回 null */
  getSubtitleStyleForPersist: () => SubtitleStyleBackendPayload | null;
  /** POST /video/subtitles/generate-video 完整请求体；未就绪时返回 null */
  getGenerateVideoRequestPayload: (taskId: number) => GenerateVideoRequestPayload | null;
  /** 按当前框宽为全部台词生成带 \\N 的 postText（合成前批量提交用） */
  buildBatchPostTexts: () => Array<{ id: number; postText: string }> | null;
}

export interface SubtitleOverlayPlayerProps {
  taskId?: string; // 任务ID，如果不提供则使用默认值
  srtContent?: string; // 自定义SRT内容，如果不提供则使用默认内容
  showControls?: boolean; // 是否显示播放控制按钮
  className?: string; // 自定义CSS类名
  videoData?: Task.GetTaskStatusResult | null; // 视频数据，包含pure_video_hls_url
  localSubtitles?: Task.SubtitlesLegacy[]; // 本地字幕数据，优先使用此数据
  targetText?: Array<{ id: number; text: string; timeRange: string }>; // 右侧台词编辑区数据，用于获取最新时间戳
  /** 有值时叠加字幕按该行 id 显示（同 subtitles[].id） */
  overlayDisplayLockLineId?: number | null;
  // 外部播放控制
  externalIsPlaying?: boolean; // 外部播放状态
  externalCurrentTime?: number; // 外部当前时间，用于同步
  onExternalPlay?: () => void; // 外部播放控制回调
  onExternalPause?: () => void; // 外部暂停控制回调
  onExternalSeek?: (time: number) => void; // 外部跳转控制回调
  onSubtitleChange?: (subtitles: Subtitle[]) => void; // 字幕变化回调
  onRightVideoReady?: () => void; // 右侧视频就绪回调
  overlayTopPx?: number; // 画布顶部偏移（px），用于根据角色调整
  // 字幕位置与缩放（归一化坐标，用于可拖拽/缩放字幕）
  subtitlePosition?: { x: number; y: number }; // 归一化坐标 0-1，锚点为中心，相对 contain 画面
  /** 纵向：字号与字幕框高度 ∝ SUBTITLE_PREVIEW_BOX_HEIGHT_FRAC * 该系数；角点拖拽改此项 */
  subtitleScale?: number;
  /** 横向：字幕框宽度 / 画面宽；左右边手柄只改此项，字号不变 */
  subtitleWidthRatio?: number;
  /** 字幕框固定行数（GET /video/task/get subtitleLineNumber；0 视为 2） */
  subtitleLineNumber?: number;
  interactive?: boolean; // 是否显示拖拽/缩放手柄
  onSubtitlePositionChange?: (pos: { x: number; y: number }) => void;
  onSubtitleScaleChange?: (scale: number) => void;
  onSubtitleWidthRatioChange?: (widthRatio: number) => void;
  /** 字幕框换行导致 postText（含 \\N）变化时同步到编辑页状态 */
  onSubtitlePostTextChange?: (payload: { id: number; postText: string }) => void;
  /** 用户点击行数箭头；返回 true 表示已持久化可更新本地布局 */
  onSubtitleLineNumberChange?: (lineNumber: number) => Promise<boolean>;
  subtitleSelected?: boolean; // 字幕是否被选中（显示选框）
  onSubtitleSelectedChange?: (selected: boolean) => void;
}

const SubtitleOverlayPlayer = forwardRef<SubtitleOverlayPlayerRef, SubtitleOverlayPlayerProps>(({
  taskId,
  srtContent,
  showControls = true,
  className = '',
  videoData,
  localSubtitles,
  targetText,
  overlayDisplayLockLineId,
  externalIsPlaying,
  externalCurrentTime,
  onExternalPlay,
  onExternalPause,
  onExternalSeek,
  onSubtitleChange,
  onRightVideoReady,
  overlayTopPx = 10,
  subtitlePosition,
  subtitleScale,
  subtitleWidthRatio,
  subtitleLineNumber,
  interactive = false,
  onSubtitlePositionChange,
  onSubtitleScaleChange,
  onSubtitleWidthRatioChange,
  onSubtitlePostTextChange,
  onSubtitleLineNumberChange,
  subtitleSelected = false,
  onSubtitleSelectedChange,
}, ref) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const trackRef = useRef<HTMLTrackElement>(null);
  const videoClipRef = useRef<HTMLDivElement>(null);
  
  // 状态管理
  const [fetchedSrtContent, setFetchedSrtContent] = useState<string>('');
  const subtitlesRef = useRef<Subtitle[]>([]);
  const [subtitles, setSubtitles] = useState<Subtitle[]>([]);
  subtitlesRef.current = subtitles;
  const resizeObserverRef = useRef<ResizeObserver | null>(null); // ResizeObserver 引用
  /** RO 在 layout 后可能同帧连发多次，合并到下一帧只跑一次 resizeCanvas */
  const resizeObserverCoalesceRafRef = useRef<number | null>(null);
  const rafIdRef = useRef<number | null>(null); // requestAnimationFrame ID
  const renderRafIdRef = useRef<number | null>(null); // 字幕渲染的 requestAnimationFrame ID
  const lastRenderTimeRef = useRef<number>(0); // 上次渲染时间
  
  // Canvas相关
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);

  // 字幕位置/缩放的 ref（用于实时拖拽，避免 React 渲染延迟）
  const subtitlePosRef = useRef(subtitlePosition ?? { x: 0.5, y: 0.85 });
  const subtitleScaleRef = useRef(subtitleScale ?? 1.0);
  const subtitleWidthRatioRef = useRef(
    subtitleWidthRatio ?? SUBTITLE_PREVIEW_BOX_WIDTH_FRAC * (subtitleScale ?? 1)
  );
  /** object-fit:contain 下「实际画面」在裁剪区内的矩形（与字幕归一化坐标同一套基准） */
  const canvasInfoRef = useRef({ offsetX: 0, offsetY: 0, w: 0, h: 0 });
  // Rnd 的位置/尺寸（state，驱动 Rnd 渲染）
  const [rndState, setRndState] = useState({ x: 0, y: 0, w: 0, h: 0 });
  /** 与 Rnd 选框同步，供 imperative API 读取实际框尺寸 */
  const rndSizeRef = useRef({ w: 0, h: 0 });
  // renderSubtitles 的 ref（解决声明顺序问题）；force 用于布局变更后绕过节流，避免 canvas 被清空后未重绘闪白
  const renderSubtitlesRef = useRef<((opts?: { force?: boolean; sync?: boolean }) => void) | null>(null);
  // 拖拽进行中标记（防止 useEffect 在拖拽期间覆盖 ref）
  const isDraggingRef = useRef(false);
  /** 角点缩放开始时锁定框的 宽/高 比，拖动过程中保持比例 */
  const subtitleCornerResizeAspectRef = useRef<number | null>(null);
  /**
   * 角点拖拽时传给 Rnd/re-resizable 的 lockAspectRatio（数值=宽/高）。
   * 拖拽中 Resizable 忽略受控 size，必须在库内锁比例；flushSync 保证在库读取 ratio 前已更新 prop。
   */
  const [rndCornerLockAspect, setRndCornerLockAspect] = useState<number | false>(false);

  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const wrapTextRef = useRef<SubtitleWrapFn | null>(null);
  const externalCurrentTimeRef = useRef<number | undefined>(undefined);
  /** 换行块尺寸缓存，避免每帧 measureText */
  const subtitleBlockLayoutCacheRef = useRef<{ key: string; bgW: number; bgH: number } | null>(null);
  /** 当前条字幕 identity，用于时间轴切 cue 时同步选框高度（多行） */
  const lastRenderedCueLayoutKeyRef = useRef<string | null>(null);
  const autoSubtitleLineNumberRef = useRef(normalizeSubtitleLineNumber(subtitleLineNumber ?? SUBTITLE_DEFAULT_LINE_NUMBER));
  const [displaySubtitleLineNumber, setDisplaySubtitleLineNumber] = useState(
    normalizeSubtitleLineNumber(subtitleLineNumber ?? SUBTITLE_DEFAULT_LINE_NUMBER)
  );
  const [lineAdjusting, setLineAdjusting] = useState(false);
  /** 当前 cue 文案 + 行数上限下的最窄框宽（px）；供 Rnd.minWidth，onResize 仅读缓存 */
  const contentMinBoxWidthPxRef = useRef(40);
  const contentMinWidthCacheKeyRef = useRef('');
  const [rndContentMinWidth, setRndContentMinWidth] = useState(40);

  const getSubtitleFontSize = useCallback((contentW: number, contentH: number) => {
    const video = videoRef.current;
    return computePreviewSubtitleDrawFontPx({
      scale: subtitleScaleRef.current,
      contentW,
      contentH,
      intrinsicVideoWidth: video && video.videoWidth > 0 ? video.videoWidth : undefined,
      intrinsicVideoHeight: video && video.videoHeight > 0 ? video.videoHeight : undefined,
    });
  }, []);

  const getFixedSubtitleBoxHeight = useCallback((fontSize: number, lineCount = autoSubtitleLineNumberRef.current) => {
    const { paddingY } = computeSubtitleBoxPadding(fontSize);
    return computeFixedSubtitleBoxHeightPx(fontSize, lineCount, paddingY);
  }, []);

  const getIntrinsicVideoSize = useCallback(() => {
    const video = videoRef.current;
    return {
      width: video && video.videoWidth > 0 ? video.videoWidth : undefined,
      height: video && video.videoHeight > 0 ? video.videoHeight : undefined,
    };
  }, []);

  const getModelSubtitleBoxSize = useCallback(
    (contentW: number, contentH: number) => {
      const fontSize = getSubtitleFontSize(contentW, contentH);
      const boxW = contentW * subtitleWidthRatioRef.current;
      const boxH = getFixedSubtitleBoxHeight(fontSize);
      return { boxW, boxH };
    },
    [getFixedSubtitleBoxHeight, getSubtitleFontSize]
  );

  const subtitleWrapExceedsLineCap = useCallback(
    (boxWPx: number, contentW: number, contentH: number): boolean => {
      const video = videoRef.current;
      const t =
        externalCurrentTimeRef.current ??
        (video && Number.isFinite(video.currentTime) ? video.currentTime : 0);
      const sub = findActiveSubtitleForOverlay(
        subtitlesRef.current,
        t,
        overlayDisplayLockLineId
      );
      if (!sub?.text?.trim()) return false;
      const c = ctxRef.current;
      if (!c) return false;
      const plain = stripSubtitleLineBreakMarkers(stripTargetTextUserLineBreaks(sub.text));
      const fontSize = getSubtitleFontSize(contentW, contentH);
      const { paddingX } = computeSubtitleBoxPadding(fontSize);
      c.font = `${fontSize}px Microsoft YaHei, Arial, sans-serif`;
      const inner = Math.max(1, boxWPx - paddingX * 2);
      const cap = autoSubtitleLineNumberRef.current;
      return !isSubtitleWrapLayoutValid(
        plain,
        inner,
        cap,
        (s) => c.measureText(s).width
      );
    },
    [getSubtitleFontSize, overlayDisplayLockLineId]
  );

  /** 角点缩放：更新 widthRatio，按锁定宽高比反推 fontScale，再 snap 到固定行数框高 */
  const applyCornerResizeFromWidth = useCallback(
    (rawW: number, contentW: number, contentH: number, minBoxWidthPx = 0) => {
      // Corner resizing scales the font too, so the pre-resize content minimum is stale.
      // Start with geometry limits; only apply a content limit recomputed at the new scale.
      const clampedW = clampSubtitleBoxWidthPx(
        rawW,
        contentW,
        minBoxWidthPx
      );
      subtitleWidthRatioRef.current = clampSubtitleBoxWidthRatio(clampedW / contentW);
      const lines = autoSubtitleLineNumberRef.current;
      const ar = subtitleCornerResizeAspectRef.current;
      const boxW = contentW * subtitleWidthRatioRef.current;
      const { paddingY } = computeSubtitleBoxPadding(
        getSubtitleFontSize(contentW, contentH)
      );
      const intrinsic = getIntrinsicVideoSize();
      const dragH = ar && ar > 0 ? boxW / ar : getFixedSubtitleBoxHeight(getSubtitleFontSize(contentW, contentH), lines);
      subtitleScaleRef.current = deriveSubtitleFontScaleFromBoxHeight(
        dragH,
        lines,
        contentW,
        contentH,
        paddingY,
        intrinsic.width,
        intrinsic.height
      );
      const fontSize = getSubtitleFontSize(contentW, contentH);
      const boxH = getFixedSubtitleBoxHeight(fontSize, lines);
      return { boxW, boxH };
    },
    [getFixedSubtitleBoxHeight, getIntrinsicVideoSize, getSubtitleFontSize]
  );

  useEffect(() => {
    ctxRef.current = ctx;
  }, [ctx]);
  useEffect(() => {
    externalCurrentTimeRef.current = externalCurrentTime;
  }, [externalCurrentTime]);

  // 根据当前 ref 计算并更新 Rnd 的位置和尺寸
  // boxW 基于 widthRatio；boxH = 固定行数 × 单行字高（与 subtitleLineNumber 一致）
  const updateRndFromRef = useCallback(() => {
    const info = canvasInfoRef.current;
    if (info.w <= 0 || info.h <= 0) return;
    const posX = subtitlePosRef.current.x;
    const posY = subtitlePosRef.current.y;
    const widthRatio = subtitleWidthRatioRef.current;
    const boxW = info.w * widthRatio;
    const fontSize = getSubtitleFontSize(info.w, info.h);
    const boxH = getFixedSubtitleBoxHeight(fontSize);

    const centerCssX = info.offsetX + posX * info.w;
    const centerCssY = info.offsetY + posY * info.h;
    setRndState((prev) => {
      const nx = centerCssX - boxW / 2;
      const ny = centerCssY - boxH / 2;
      if (
        Math.abs(prev.x - nx) < 0.5 &&
        Math.abs(prev.y - ny) < 0.5 &&
        Math.abs(prev.w - boxW) < 0.5 &&
        Math.abs(prev.h - boxH) < 0.5
      ) {
        return prev;
      }
      return { x: nx, y: ny, w: boxW, h: boxH };
    });
    rndSizeRef.current = { w: boxW, h: boxH };
  }, [getFixedSubtitleBoxHeight, getSubtitleFontSize]);

  const recomputeContentMinBoxWidth = useCallback(
    (active?: Subtitle | null) => {
      const info = canvasInfoRef.current;
      if (info.w <= 0 || info.h <= 0) return;

      const video = videoRef.current;
      const t =
        externalCurrentTimeRef.current ??
        (video && Number.isFinite(video.currentTime) ? video.currentTime : 0);
      const sub =
        active ??
        findActiveSubtitleForOverlay(
          subtitlesRef.current,
          t,
          overlayDisplayLockLineId
        );

      const fontSize = getSubtitleFontSize(info.w, info.h);
      const c = ctxRef.current;
      let minW = Math.max(40, Math.round(info.w * SUBTITLE_BOX_WIDTH_RATIO_MIN));
      if (c && sub?.text?.trim()) {
        c.font = `${fontSize}px Microsoft YaHei, Arial, sans-serif`;
        const plainForMeasure = stripSubtitleLineBreakMarkers(
          stripTargetTextUserLineBreaks(sub.text)
        );
        minW = computeMinSubtitleBoxWidthPx({
          text: plainForMeasure,
          lineCap: autoSubtitleLineNumberRef.current,
          fontSizePx: fontSize,
          contentW: info.w,
          measureTextWidth: (s) => c.measureText(s).width,
        });
      }

      contentMinBoxWidthPxRef.current = minW;
      setRndContentMinWidth(minW);

      const boxW = info.w * subtitleWidthRatioRef.current;
      if (boxW < minW - 0.5) {
        subtitleWidthRatioRef.current = clampSubtitleBoxWidthRatio(minW / info.w);
        subtitleBlockLayoutCacheRef.current = null;
        if (!isDraggingRef.current) {
          updateRndFromRef();
          onSubtitleWidthRatioChange?.(subtitleWidthRatioRef.current);
        }
      }
    },
    [
      getSubtitleFontSize,
      overlayDisplayLockLineId,
      updateRndFromRef,
      onSubtitleWidthRatioChange,
    ]
  );

  const getSubtitleMaxTextWidthPx = useCallback(
    (contentW: number, contentH: number) => {
      const fontSize = getSubtitleFontSize(contentW, contentH);
      const { paddingX } = computeSubtitleBoxPadding(fontSize);
      return Math.max(1, contentW * subtitleWidthRatioRef.current - paddingX * 2);
    },
    [getSubtitleFontSize]
  );

  const applyPostTextToSubtitleId = useCallback((subtitleId: number, postText: string) => {
    const patch = (list: Subtitle[]) =>
      list.map((s) => (s.id === subtitleId ? { ...s, text: postText } : s));
    subtitlesRef.current = patch(subtitlesRef.current);
    setSubtitles((prev) => patch(prev));
    subtitleBlockLayoutCacheRef.current = null;
  }, []);

  const syncActiveSubtitlePostTextFromBox = useCallback(() => {
    const info = canvasInfoRef.current;
    const c = ctxRef.current;
    if (info.w <= 0 || info.h <= 0 || !c) return;

    const video = videoRef.current;
    const t =
      externalCurrentTimeRef.current ??
      (video && Number.isFinite(video.currentTime) ? video.currentTime : 0);
    const active = findActiveSubtitleForOverlay(
      subtitlesRef.current,
      t,
      overlayDisplayLockLineId
    );
    if (active?.id == null) return;

    const fontSize = getSubtitleFontSize(info.w, info.h);
    c.font = `${fontSize}px Microsoft YaHei, Arial, sans-serif`;
    const maxTextWidth = getSubtitleMaxTextWidthPx(info.w, info.h);
    const nextPost = buildPostTextFromPlainAndWrap(
      active.text,
      maxTextWidth,
      (s) => c.measureText(s).width
    );
    if (nextPost === active.text) return;

    applyPostTextToSubtitleId(active.id, nextPost);
    contentMinWidthCacheKeyRef.current = '';
    recomputeContentMinBoxWidth();
    onSubtitlePostTextChange?.({ id: active.id, postText: nextPost });
    renderSubtitlesRef.current?.({ force: true });
  }, [
    applyPostTextToSubtitleId,
    getSubtitleFontSize,
    getSubtitleMaxTextWidthPx,
    overlayDisplayLockLineId,
    onSubtitlePostTextChange,
    recomputeContentMinBoxWidth,
  ]);

  const buildBatchPostTextsRef = useRef<() => Array<{ id: number; postText: string }> | null>(
    () => null
  );
  buildBatchPostTextsRef.current = () => {
    const c = ctxRef.current;
    const info = canvasInfoRef.current;
    if (info.w <= 0 || info.h <= 0 || !c) return null;

    const fontSize = getSubtitleFontSize(info.w, info.h);
    c.font = `${fontSize}px Microsoft YaHei, Arial, sans-serif`;
    const maxTextWidth = getSubtitleMaxTextWidthPx(info.w, info.h);

    const result: Array<{ id: number; postText: string }> = [];
    for (const s of subtitlesRef.current) {
      if (s.id == null) continue;
      result.push({
        id: s.id,
        postText: buildPostTextFromPlainAndWrap(
          s.text,
          maxTextWidth,
          (line) => c.measureText(line).width
        ),
      });
    }
    return result.length > 0 ? result : null;
  };

  const recalculateLayoutForLineNumber = useCallback(
    (nextLineCount: number) => {
      const info = canvasInfoRef.current;
      if (info.w <= 0 || info.h <= 0) return;

      const fontSize = getSubtitleFontSize(info.w, info.h);
      const boxW = info.w * subtitleWidthRatioRef.current;
      const newBoxH = getFixedSubtitleBoxHeight(fontSize, nextLineCount);

      subtitleCornerResizeAspectRef.current = computeSubtitleCornerLockAspect(boxW, newBoxH);
      setRndCornerLockAspect(false);
      subtitleBlockLayoutCacheRef.current = null;
      contentMinWidthCacheKeyRef.current = '';
      recomputeContentMinBoxWidth();

      updateRndFromRef();
      syncActiveSubtitlePostTextFromBox();
      renderSubtitlesRef.current?.({ force: true });
    },
    [
      getFixedSubtitleBoxHeight,
      getSubtitleFontSize,
      recomputeContentMinBoxWidth,
      syncActiveSubtitlePostTextFromBox,
      updateRndFromRef,
    ]
  );

  useEffect(() => {
    const next = normalizeSubtitleLineNumber(subtitleLineNumber ?? SUBTITLE_DEFAULT_LINE_NUMBER);
    const prev = autoSubtitleLineNumberRef.current;
    autoSubtitleLineNumberRef.current = next;
    setDisplaySubtitleLineNumber(next);
    if (next !== prev && !isDraggingRef.current) {
      recalculateLayoutForLineNumber(next);
    }
  }, [subtitleLineNumber, recalculateLayoutForLineNumber]);

  const handleLineNumberAdjust = useCallback(
    async (delta: 1 | -1, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (lineAdjusting || isDraggingRef.current) return;
      const next = clampSubtitleAdjustableLineNumber(displaySubtitleLineNumber + delta);
      if (next === displaySubtitleLineNumber) return;

      setLineAdjusting(true);
      try {
        if (onSubtitleLineNumberChange) {
          const ok = await onSubtitleLineNumberChange(next);
          if (!ok) return;
        } else {
          autoSubtitleLineNumberRef.current = next;
          setDisplaySubtitleLineNumber(next);
          recalculateLayoutForLineNumber(next);
        }
      } finally {
        setLineAdjusting(false);
      }
    },
    [
      displaySubtitleLineNumber,
      lineAdjusting,
      onSubtitleLineNumberChange,
      recalculateLayoutForLineNumber,
    ]
  );

  // 同步 props → ref，更新 Rnd，并触发 canvas 重绘（拖拽期间跳过，避免覆盖用户操作）
  useEffect(() => {
    if (isDraggingRef.current) return;
    if (subtitlePosition) subtitlePosRef.current = subtitlePosition;
    if (subtitleScale !== undefined) subtitleScaleRef.current = subtitleScale;
    if (subtitleWidthRatio !== undefined) subtitleWidthRatioRef.current = subtitleWidthRatio;
    subtitleBlockLayoutCacheRef.current = null;
    contentMinWidthCacheKeyRef.current = '';
    recomputeContentMinBoxWidth();
    updateRndFromRef();
    renderSubtitlesRef.current?.();
  }, [
    subtitlePosition,
    subtitleScale,
    subtitleWidthRatio,
    subtitleLineNumber,
    subtitles,
    recomputeContentMinBoxWidth,
    updateRndFromRef,
  ]);

  useEffect(() => {
    if (!ctx) return;
    contentMinWidthCacheKeyRef.current = '';
    recomputeContentMinBoxWidth();
  }, [ctx, recomputeContentMinBoxWidth]);

  useEffect(() => {
    contentMinWidthCacheKeyRef.current = '';
    recomputeContentMinBoxWidth();
  }, [overlayDisplayLockLineId, recomputeContentMinBoxWidth]);

  // 点击 canvas 检测是否命中字幕区域
  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!interactive || !onSubtitleSelectedChange) return;
      const canvasEl = canvasRef.current;
      if (!canvasEl) return;
      const rect = canvasEl.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;
      const info = canvasInfoRef.current;
      if (info.w <= 0 || info.h <= 0) return;
      const posX = subtitlePosRef.current.x;
      const posY = subtitlePosRef.current.y;
      const widthRatio = subtitleWidthRatioRef.current;
      const boxW = info.w * widthRatio;
      const fontSize = getSubtitleFontSize(info.w, info.h);
      const boxH = getFixedSubtitleBoxHeight(fontSize);

      const centerX = info.offsetX + posX * info.w;
      const centerY = info.offsetY + posY * info.h;
      const hitX = clickX >= centerX - boxW / 2 && clickX <= centerX + boxW / 2;
      const hitY = clickY >= centerY - boxH / 2 && clickY <= centerY + boxH / 2;
      if (hitX && hitY) {
        onSubtitleSelectedChange(true);
      } else {
        onSubtitleSelectedChange(false);
      }
    },
    [interactive, onSubtitleSelectedChange, getFixedSubtitleBoxHeight, getSubtitleFontSize]
  );

  // 点击播放器外部 → 取消选中
  useEffect(() => {
    if (!interactive || !onSubtitleSelectedChange) return;
    const handleOutsideClick = (e: MouseEvent) => {
      const container = document.querySelector('.subtitle-overlay-player-container');
      if (container && !container.contains(e.target as Node)) {
        onSubtitleSelectedChange(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [interactive, onSubtitleSelectedChange]);

  // 暴露直接控制方法给父组件
  useImperativeHandle(ref, () => ({
    seekToTime: (time: number) => {
      // 直接控制跳转到时间
      if (videoRef.current) {
        videoRef.current.currentTime = time;
      }
    },
    play: () => {
      // 直接控制播放
      if (videoRef.current) {
        videoRef.current.play().catch(error => {
          console.error('SubtitleOverlayPlayer: 直接播放失败:', error);
        });
      }
    },
    pause: () => {
      // 直接控制暂停
      if (videoRef.current) {
        videoRef.current.pause();
      }
    },
    getCurrentTime: () => {
      return videoRef.current?.currentTime || 0;
    },
    getDuration: () => {
      return videoRef.current?.duration || 0;
    },
    isPlaying: () => {
      return videoRef.current ? !videoRef.current.paused : false;
    },
    getSubtitleStyleForPersist: () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas) return null;
      const info = canvasInfoRef.current;
      const contentW = info.w > 0 ? info.w : canvas.width;
      const contentH = info.h > 0 ? info.h : canvas.height;
      const snapshot = buildSubtitleStylePersistSnapshot({
        relativeCenter: { ...subtitlePosRef.current },
        fontScale: subtitleScaleRef.current,
        boxWidthRatio: subtitleWidthRatioRef.current,
        contentW,
        contentH,
        intrinsicVideoWidth: video.videoWidth || 0,
        intrinsicVideoHeight: video.videoHeight || 0,
      });
      return snapshot ? buildSubtitleStyleBackendPayload(snapshot) : null;
    },
    getGenerateVideoRequestPayload: (taskIdNum: number) => buildGenerateVideoPayloadRef.current(taskIdNum),
    buildBatchPostTexts: () => buildBatchPostTextsRef.current(),
  }), []);

  // 默认SRT字幕内容
  const defaultSrtContent = `1
00:00:01,000 --> 00:00:04,000
欢迎观看SRT字幕演示

2
00:00:04,500 --> 00:00:08,000
这是使用SRT技术实现的可编辑字幕

3
00:00:08,500 --> 00:00:12,000
支持多种样式和特效

4
00:00:12,500 --> 00:00:16,000
这是说话人1的台词
使用不同的颜色和背景

5
00:00:16,500 --> 00:00:20,000
这是说话人2的台词
每个说话人都有独特的样式

6
00:00:20,500 --> 00:00:24,000
这是高亮显示的重要信息
背景色和文字颜色都不同

7
00:00:24,500 --> 00:00:28,000
字幕支持实时编辑和更新
无需重新加载视频

8
00:00:28,500 --> 00:00:32,000
感谢观看SRT字幕演示！`;

  // 使用获取到的字幕内容、传入的SRT内容或默认内容 - 使用useMemo优化
  const currentSrtContent = useMemo(() => {
    return fetchedSrtContent || srtContent || defaultSrtContent;
  }, [fetchedSrtContent, srtContent]);

  // 解析时间格式 - 与videoEdit中的parseTimeToSeconds保持一致
  const parseTime = useCallback((timeString: string): number => {
    // 支持格式: "00:00:22,566" 或 "00:00:22.566"
    const timeStr = timeString.replace(',', '.');
    const parts = timeStr.split(':');
    
    if (parts.length === 3) {
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      const seconds = parseFloat(parts[2]);
      return hours * 3600 + minutes * 60 + seconds;
    } else if (parts.length === 2) {
      // 支持 "00:22.566" 格式
      const minutes = parseInt(parts[0], 10);
      const seconds = parseFloat(parts[1]);
      return minutes * 60 + seconds;
    } else {
      // 纯秒数格式
      return parseFloat(timeStr) || 0;
    }
  }, []);

  // 解析字幕数据 - 优先使用videoData.subtitles，回退到SRT内容
  const parseSubtitles = useCallback(() => {
    let parsedSubtitles: Subtitle[] = [];
    
    // 优先使用videoData.subtitles数据
    if (videoData?.subtitles && Array.isArray(videoData.subtitles)) {
      // console.log('SubtitleOverlayPlayer: 使用videoData.subtitles数据:', videoData.subtitles);
      
      // 创建targetText的Map，用于快速查找最新时间戳和文本
      const targetTextMap = new Map<number, { timeRange?: string; text?: string }>();
      if (targetText && Array.isArray(targetText)) {
        targetText.forEach(item => {
          targetTextMap.set(item.id, { 
            timeRange: item.timeRange,
            text: item.text
          });
        });
      }

      parsedSubtitles = videoData.subtitles.map((subtitle: Task.SubtitlesLegacy, index: number) => {
        // 优先使用targetText中的最新时间戳和文本（确保与右侧台词编辑区保持一致）
        const targetData = targetTextMap.get(subtitle.id);
        let startTime: string;
        let endTime: string;
        let text: string;

        if (targetData && targetData.timeRange) {
          // 使用targetText中的最新时间戳
          const timeRangeParts = targetData.timeRange.split(' → ');
          if (timeRangeParts.length === 2) {
            startTime = timeRangeParts[0].trim();
            endTime = timeRangeParts[1].trim();
          } else {
            // timeRange格式错误，回退到videoData
            startTime = subtitle.post_start_time || subtitle.origin_start_time;
            endTime = subtitle.post_end_time || subtitle.origin_end_time;
          }
        } else {
          // targetText中没有该字幕，使用videoData中的时间戳
          // 优先使用post时间戳，如果没有则回退到origin时间戳
          startTime = subtitle.post_start_time || subtitle.origin_start_time;
          endTime = subtitle.post_end_time || subtitle.origin_end_time;
        }

        // 右侧编辑区有该 id 时以 targetText 为准（含空字符串「置空译文」）；不能用 ||，否则 '' 会回退到 post_text
        text = targetTextMap.has(subtitle.id)
          ? (targetData?.text ?? '')
          : (subtitle.post_text || '');

        return {
          start: parseTime(startTime),
          end: parseTime(endTime),
          text: text, // 优先使用targetText中的文本
          id: subtitle.id || index + 1
        };
      });
      
      // console.log('SubtitleOverlayPlayer: 从videoData.subtitles解析的字幕数据:', parsedSubtitles);
    } else {
      // 回退到SRT内容解析
      // console.log('SubtitleOverlayPlayer: 回退到SRT内容解析');
      
      const lines = currentSrtContent.split('\n');
      let currentSubtitle: Subtitle | null = null;
      let isTimeLine = false;
      
      lines.forEach(line => {
        const trimmedLine = line.trim();
        
        // 跳过空行
        if (!trimmedLine) {
          return;
        }
        
        // 检查是否是序号行（纯数字）
        if (/^\d+$/.test(trimmedLine)) {
          // 如果遇到新的序号，保存当前字幕
          if (currentSubtitle && currentSubtitle.text) {
            parsedSubtitles.push({ ...currentSubtitle });
          }
          // 开始新的字幕块
          currentSubtitle = null;
          isTimeLine = false;
          return;
        }
        
        // 检查是否是时间行
        if (trimmedLine.includes('-->')) {
          const [start, end] = trimmedLine.split(' --> ');
          currentSubtitle = {
            start: parseTime(start.trim()),
            end: parseTime(end.trim()),
            text: ''
          };
          isTimeLine = true;
        } else if (isTimeLine && currentSubtitle) {
          // 时间行后的内容就是字幕文本
          if (currentSubtitle.text) {
            currentSubtitle.text += '\n' + trimmedLine;
          } else {
            currentSubtitle.text = trimmedLine;
          }
        } else if (currentSubtitle && !isTimeLine) {
          // 继续添加文本内容
          if (currentSubtitle.text) {
            currentSubtitle.text += '\n' + trimmedLine;
          } else {
            currentSubtitle.text = trimmedLine;
          }
        }
      });
      
      // 保存最后一个字幕
      if (currentSubtitle && (currentSubtitle as Subtitle).text) {
        const subtitle: Subtitle = {
          start: (currentSubtitle as Subtitle).start,
          end: (currentSubtitle as Subtitle).end,
          text: (currentSubtitle as Subtitle).text
        };
        parsedSubtitles.push(subtitle);
      }
      
      // 从SRT内容解析的字幕数据
    }
    
    setSubtitles(parsedSubtitles);
    
    // 通知父组件字幕数据变化
    if (onSubtitleChange) {
      onSubtitleChange(parsedSubtitles);
    }
  }, [parseTime, onSubtitleChange, currentSrtContent, videoData, targetText]);

  // 获取字幕内容
  const fetchSubtitleContent = useCallback(async (): Promise<string> => {
    try {
      // 优先使用本地字幕数据
      if (localSubtitles && localSubtitles.length > 0) {
        console.log('使用本地字幕数据，字幕数量:', localSubtitles.length);
        // 将本地字幕数据转换为SRT格式
        const srtContent = localSubtitles.map((subtitle, index) => {
          const startTime = subtitle.post_start_time || subtitle.origin_start_time;
          const endTime = subtitle.post_end_time || subtitle.origin_end_time;
          const text = subtitle.post_text || ''; // 当目标台词为空时，不显示原台词
          
          // 转换时间格式从 "00:00:00,000" 到 "00:00:00.000"
          const formatTime = (time: string) => time.replace(',', '.');
          
          return `${index + 1}\n${formatTime(startTime)} --> ${formatTime(endTime)}\n${text}\n`;
        }).join('\n');
        
        return srtContent;
      }
      
      // 如果没有本地字幕数据，优先使用传入的videoData中的翻译后字幕
      if (videoData && videoData.translated_subtitle_url) {
        // 与任务状态接口一致：字幕走 /storage（或已是完整 URL），勿再拼 /media 前缀
        const subtitleUrl = processVideoUrl(videoData.translated_subtitle_url);
        console.log('获取翻译后字幕内容:', subtitleUrl);
        
        const response = await fetch(subtitleUrl);
        if (response.ok) {
          const content = await response.text();
          // console.log('翻译后字幕内容获取成功:', content.substring(0, 100) + '...');
          return content;
        } else {
          throw new Error(`翻译后字幕获取失败: ${response.status}`);
        }
      }
      
      // 如果没有翻译后字幕，尝试使用原始字幕
      if (videoData && videoData.subtitle_url) {
        const subtitleUrl = processVideoUrl(videoData.subtitle_url);
        
        const response = await fetch(subtitleUrl);
        if (response.ok) {
          const content = await response.text();
          // console.log('原始字幕内容获取成功:', content.substring(0, 100) + '...');
          return content;
        } else {
          throw new Error(`原始字幕获取失败: ${response.status}`);
        }
      }
      
      // 如果都没有，使用默认字幕
      // console.log('使用默认字幕内容');
      return defaultSrtContent;
    } catch (error) {
      console.error('获取字幕内容失败:', error);
      // 如果获取失败，使用默认字幕
      return defaultSrtContent;
    }
  }, [videoData, localSubtitles]);

  // 获取视频URL
  const fetchVideoUrl = useCallback(async (): Promise<string> => {
    try {
      if (videoData && videoData.pure_video_hls_url) {
        console.log('使用传入的videoData中的视频URL:', videoData.pure_video_hls_url);
        return videoData.pure_video_hls_url;
      }
      // 无纯净流时回退到合成 HLS，避免右侧永远不触发 loadedmetadata、左侧 init 死等 rightVideoReady
      if (videoData && videoData.video_hls_url) {
        console.log('无 pure_video_hls_url，回退使用 video_hls_url:', videoData.video_hls_url);
        return videoData.video_hls_url;
      }
      
      // 如果没有传入videoData，且没有taskId，则使用默认视频
      if (!taskId) {
        console.log('没有传入taskId，使用默认视频');
        return 'https://www.w3schools.com/html/mov_bbb.mp4';
      }
      
      // 如果有taskId，则通过API获取
      const response = await getTaskStatusApi(parseInt(taskId));
      const apiPure = response?.pure_video_hls_url ?? response?.pureVideoHlsUrl;
      const apiComposite = response?.video_hls_url ?? response?.videoHlsUrl;
      if (apiPure) {
        return apiPure;
      }
      if (apiComposite) {
        console.log('API 无 pure_video_hls_url，回退使用 video_hls_url:', apiComposite);
        return apiComposite;
      }
      throw new Error('未找到视频URL');
    } catch (error) {
      console.error('获取视频URL失败:', error);
      // 如果API调用失败，使用默认视频
      return 'https://www.w3schools.com/html/mov_bbb.mp4';
    }
  }, [taskId, videoData]);

  // 初始化视频
  const initVideo = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    
    try {
      const url = await fetchVideoUrl();
      
      // 设置视频源
      if (url.endsWith('.m3u8')) {
        // HLS视频流
        if (video.canPlayType('application/vnd.apple.mpegurl')) {
          // Safari原生支持HLS
          video.src = url;
          console.log('使用Safari原生HLS支持');
        } else if (Hls.isSupported()) {
          // 使用HLS.js库
          const hls = new Hls();
          hls.loadSource(url);
          hls.attachMedia(video);
          console.log('使用HLS.js库播放HLS视频流');
        } else {
          console.error('浏览器不支持HLS视频播放');
          video.src = url; // 尝试直接设置，可能会失败
        }
      } else {
        // 普通视频文件
        video.src = url;
        console.log('播放普通视频文件');
      }
      
      // 预加载视频元数据，确保readyState >= 1
      video.preload = 'metadata';
      video.load(); // 强制加载元数据
      
      console.log('视频源已设置:', url);
      
    } catch (error) {
      console.error('初始化视频失败:', error);
    }
  }, [fetchVideoUrl]);

  // 初始化字幕
  const initSubtitles = useCallback(() => {
    const video = videoRef.current;
    const track = trackRef.current;
    
    if (!video || !track) return;
    
    // 创建Blob URL
    const blob = new Blob([currentSrtContent], { type: 'text/srt' });
    const url = URL.createObjectURL(blob);
    
    // 设置字幕轨道源
    track.src = url;
    // 默认隐藏原生字幕，使用Canvas模式
    (track as any).mode = 'hidden';
    
    // 解析字幕数据
    parseSubtitles();
  }, [parseSubtitles]);

  const wrapText = useCallback((text: string, maxWidth: number, ctx: CanvasRenderingContext2D, maxLines?: number): string[] => {
    return wrapSubtitleTextByTokens(text, maxWidth, (s) => ctx.measureText(s).width, maxLines);
  }, []);

  /** 当前时间点字幕文字块高度（content 坐标），与 canvas 绘制一致 */
  const resolveTextBlockHeightPx = useCallback((): number | undefined => {
    const c = ctxRef.current;
    const wf = wrapTextRef.current;
    const video = videoRef.current;
    const info = canvasInfoRef.current;
    if (!c || !wf || !video || info.w <= 0 || info.h <= 0) return undefined;

    const t =
      externalCurrentTimeRef.current !== undefined && externalCurrentTimeRef.current !== null
        ? Number(externalCurrentTimeRef.current)
        : video.currentTime;
    const active = findActiveSubtitleForOverlay(
      subtitlesRef.current,
      t,
      overlayDisplayLockLineId
    );
    const trimmed = active?.text
      ? stripSubtitleLineBreakMarkers(stripTargetTextUserLineBreaks(active.text)).trim()
      : '';
    if (!trimmed) return undefined;

    const metrics = computeSubtitleDrawBlockMetrics(
      c,
      trimmed,
      subtitleScaleRef.current,
      subtitleWidthRatioRef.current,
      info,
      wf,
      video.videoWidth > 0 ? video.videoWidth : undefined,
      video.videoHeight > 0 ? video.videoHeight : undefined
    );
    return metrics?.bgH;
  }, []);

  const buildGenerateVideoPayloadRef = useRef<(taskIdNum: number) => ReturnType<typeof buildGenerateVideoRequestPayloadFromStyleState>>(() => null);
  buildGenerateVideoPayloadRef.current = (taskIdNum: number) => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return null;
    const info = canvasInfoRef.current;
    const contentW = info.w > 0 ? info.w : canvas.width;
    const contentH = info.h > 0 ? info.h : canvas.height;
    return buildGenerateVideoRequestPayloadFromStyleState({
      taskId: taskIdNum,
      relativeCenter: { ...subtitlePosRef.current },
      fontScale: subtitleScaleRef.current,
      boxWidthRatio: subtitleWidthRatioRef.current,
      contentW,
      contentH,
      intrinsicVideoWidth: video.videoWidth || 0,
      intrinsicVideoHeight: video.videoHeight || 0,
      previewBoxWidthPx: rndSizeRef.current.w,
      previewBoxHeightPx: rndSizeRef.current.h,
      textBlockHeightPx: resolveTextBlockHeightPx(),
    });
  };

  // 渲染单个字幕的函数（供 renderSubtitles 使用）
  // 读取 ref 而非 props，确保拖拽时无需等待 React 重渲染即可立即绘制
  const renderSubtitleInternal = useCallback((subtitle: Subtitle, index: number) => {
    if (!ctx || !canvas) return;

    const posX = subtitlePosRef.current.x;
    const posY = subtitlePosRef.current.y;
    const fontScale = subtitleScaleRef.current;
    const widthRatio = subtitleWidthRatioRef.current;

    const clipW = canvas.width || canvas.clientWidth;
    const clipH = canvas.height || canvas.clientHeight;
    const info = canvasInfoRef.current;
    const contentW = info.w > 0 ? info.w : clipW;
    const contentH = info.h > 0 ? info.h : clipH;
    const ox = info.w > 0 ? info.offsetX : 0;
    const oy = info.h > 0 ? info.offsetY : 0;

    const baseLineHeightRatio = SUBTITLE_PREVIEW_LINE_HEIGHT_RATIO;
    const baseStrokeWidthRatio = SUBTITLE_PREVIEW_STROKE_TO_FONT_FRAC;

    const vid = videoRef.current;
    const iw = vid && vid.videoWidth > 0 ? vid.videoWidth : undefined;
    const ih = vid && vid.videoHeight > 0 ? vid.videoHeight : undefined;
    const fontSize = computePreviewSubtitleDrawFontPx({
      scale: fontScale,
      contentW,
      contentH,
      intrinsicVideoWidth: iw,
      intrinsicVideoHeight: ih,
    });
    const { paddingX, paddingY } = computeSubtitleBoxPadding(fontSize);
    const lineHeight = fontSize * baseLineHeightRatio;
    const strokeWidth = Math.max(0.5, fontSize * baseStrokeWidthRatio);
    const maxWidth = contentW * widthRatio - paddingX * 2;

    ctx.font = `${fontSize}px Microsoft YaHei, Arial, sans-serif`;

    const plain = stripSubtitleLineBreakMarkers(stripTargetTextUserLineBreaks(subtitle.text));
    const lines = wrapText(plain, maxWidth, ctx, autoSubtitleLineNumberRef.current);

    const bgWidth = contentW * widthRatio;
    const bgHeight = getFixedSubtitleBoxHeight(fontSize);

    const baseX = ox + posX * contentW;
    const baseY = oy + posY * contentH;
    const x = baseX - bgWidth / 2;
    const y = baseY - bgHeight / 2;

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const textColor = '#fff';

    const startY = y + bgHeight / 2 - (lines.length - 1) * lineHeight / 2;

    lines.forEach((line, lineIndex) => {
      const lineY = startY + lineIndex * lineHeight;
      const textX = x + bgWidth / 2;

      ctx.strokeStyle = '#000000';
      ctx.lineWidth = strokeWidth;
      ctx.lineJoin = 'round';
      ctx.miterLimit = 2;
      ctx.strokeText(line, textX, lineY);

      ctx.fillStyle = textColor;
      ctx.fillText(line, textX, lineY);
    });
  }, [ctx, canvas, wrapText, getFixedSubtitleBoxHeight]);

  // 渲染字幕到Canvas（优化版本，避免闪烁）
  const renderSubtitles = useCallback((opts?: { force?: boolean; sync?: boolean }) => {
    wrapTextRef.current = wrapText;
    if (!ctx || !canvas || !videoRef.current) return;

    const runPaint = () => {
      const video = videoRef.current;
      if (!video || !canvas || !ctx) return;

      const currentTime = externalCurrentTime !== undefined ? externalCurrentTime : video.currentTime;

      const now = performance.now();
      const force = Boolean(opts?.force);
      if (!force && now - lastRenderTimeRef.current < 16) {
        return;
      }
      lastRenderTimeRef.current = now;

      // canvas 改尺寸后上下文被浏览器重置，必须先恢复安全默认再 clear / 绘制
      primeSubtitleCanvas2DContext(ctx);

      ctx.save();
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 渲染当前时间段的字幕（只渲染第一个匹配的字幕，避免同时显示多个）
      // overlayDisplayLockLineId 仅决定「显示哪一条」，仍须在 start~end 内才绘制
      let cueLayoutKey: string | null = null;
      const lockId = overlayDisplayLockLineId;
      const isInRange = (s: Subtitle) =>
        currentTime >= s.start - SUBTITLE_OVERLAY_DISPLAY_LEAD_SEC && currentTime < s.end;

      let rendered = false;
      if (lockId != null && lockId > 0) {
        const locked = subtitles.find((s) => s.id === lockId);
        if (locked?.text && isInRange(locked)) {
          const idx = subtitles.indexOf(locked);
          renderSubtitleInternal(locked, idx);
          cueLayoutKey = `${locked.start}|${locked.end}|${locked.text}`;
          rendered = true;
        }
      }

      if (!rendered) {
        for (let index = 0; index < subtitles.length; index++) {
          const subtitle = subtitles[index];
          // 使用 < end 而不是 <= end，避免在边界处同时匹配两个字幕
          if (isInRange(subtitle)) {
            renderSubtitleInternal(subtitle, index);
            cueLayoutKey = `${subtitle.start}|${subtitle.end}|${subtitle.text}`;
            break;
          }
        }
      }
      if (cueLayoutKey !== lastRenderedCueLayoutKeyRef.current) {
        lastRenderedCueLayoutKeyRef.current = cueLayoutKey;
        if (!isDraggingRef.current) {
          const info = canvasInfoRef.current;
          const cacheKey = `${cueLayoutKey ?? ''}|${autoSubtitleLineNumberRef.current}|${subtitleScaleRef.current}|${info.w}`;
          if (cacheKey !== contentMinWidthCacheKeyRef.current) {
            contentMinWidthCacheKeyRef.current = cacheKey;
            recomputeContentMinBoxWidth();
          }
          updateRndFromRef();
        }
      }

      ctx.restore();
    };

    if (opts?.sync) {
      if (renderRafIdRef.current !== null) {
        cancelAnimationFrame(renderRafIdRef.current);
        renderRafIdRef.current = null;
      }
      runPaint();
      return;
    }

    if (renderRafIdRef.current !== null) {
      cancelAnimationFrame(renderRafIdRef.current);
    }

    renderRafIdRef.current = requestAnimationFrame(() => {
      renderRafIdRef.current = null;
      runPaint();
    });
  }, [
    ctx,
    canvas,
    subtitles,
    externalCurrentTime,
    overlayDisplayLockLineId,
    renderSubtitleInternal,
    recomputeContentMinBoxWidth,
    updateRndFromRef,
    wrapText,
  ]);

  // 同步 renderSubtitles 到 ref，供 useEffect 和拖拽处理器调用
  renderSubtitlesRef.current = renderSubtitles;

  // 调整Canvas尺寸和位置（优化版本，避免闪烁）
  const resizeCanvas = useCallback(() => {
    const video = videoRef.current;
    const canvasElement = canvasRef.current;
    const clipEl = videoClipRef.current;

    if (!video || !canvasElement || !clipEl) return;

    // 取消之前的动画帧请求
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }

    // 使用requestAnimationFrame确保在下一帧更新，实现流畅的实时响应
    rafIdRef.current = requestAnimationFrame(() => {
      const videoRect = video.getBoundingClientRect();
      const containerRect = clipEl.parentElement?.getBoundingClientRect();

      if (!containerRect) {
        rafIdRef.current = null;
        return;
      }

      const newWidth = Math.round(videoRect.width);
      const newHeight = Math.round(videoRect.height);

      if (newWidth <= 0 || newHeight <= 0) {
        rafIdRef.current = null;
        return;
      }

      const clipPosX = videoRect.left - containerRect.left;
      const clipPosY = videoRect.top - containerRect.top;

      const prevInfo = canvasInfoRef.current;

      let contentOffsetX = 0;
      let contentOffsetY = 0;
      let contentW = newWidth;
      let contentH = newHeight;
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        const fit = getContainFitTransform(video.videoWidth, video.videoHeight, newWidth, newHeight);
        contentOffsetX = fit.offsetX;
        contentOffsetY = fit.offsetY;
        contentW = fit.displayedW;
        contentH = fit.displayedH;
      }

      /** 仅包住 contain 内实际画面，避免画布宽度=video 布局盒导致字幕可拖进两侧留黑区 */
      const cw = Math.max(1, Math.round(contentW));
      const ch = Math.max(1, Math.round(contentH));
      const clipLeft = Math.round(clipPosX + contentOffsetX);
      const clipTop = Math.round(clipPosY + contentOffsetY);

      const sizeChanged = canvasElement.width !== cw || canvasElement.height !== ch;
      // 仅尺寸变化时重置 buffer（否则会整画布清空，且易与节流叠加产生闪白）
      if (sizeChanged) {
        canvasElement.width = cw;
        canvasElement.height = ch;
      }
      canvasElement.style.width = `${cw}px`;
      canvasElement.style.height = `${ch}px`;

      clipEl.style.left = `${clipLeft}px`;
      clipEl.style.top = `${clipTop}px`;
      clipEl.style.width = `${cw}px`;
      clipEl.style.height = `${ch}px`;
      canvasElement.style.left = '0px';
      canvasElement.style.top = '0px';

      canvasInfoRef.current = {
        offsetX: 0,
        offsetY: 0,
        w: cw,
        h: ch,
      };
      contentMinWidthCacheKeyRef.current = '';
      recomputeContentMinBoxWidth();
      updateRndFromRef();

      const geometryChanged =
        sizeChanged ||
        prevInfo.w !== cw ||
        prevInfo.h !== ch ||
        prevInfo.offsetX !== 0 ||
        prevInfo.offsetY !== 0;

      // 布局或 contain 矩形变化后同帧立刻重绘（sync），避免 canvas 被 reset 后多等一帧空白 + 默认 ctx 态
      if (geometryChanged) {
        renderSubtitles({ force: true, sync: true });
      }

      rafIdRef.current = null;
    });
  }, [renderSubtitles, recomputeContentMinBoxWidth, updateRndFromRef]);

  // 初始化Canvas
  const initCanvas = useCallback(() => {
    const canvasElement = canvasRef.current;
    if (!canvasElement) return;
    
    const context = canvasElement.getContext('2d');
    if (!context) return;
    
    setCanvas(canvasElement);
    setCtx(context);
    
    // 设置Canvas尺寸
    resizeCanvas();
  }, [resizeCanvas]);




  // 渲染单个字幕（legacy，保留兼容）
  const renderSubtitle = useCallback((subtitle: Subtitle, index: number) => {
    if (!ctx || !canvas) return;

    const posX = subtitlePosRef.current.x;
    const posY = subtitlePosRef.current.y;
    const fontScale = subtitleScaleRef.current;
    const widthRatio = subtitleWidthRatioRef.current;

    const clipW = canvas.width || canvas.clientWidth;
    const clipH = canvas.height || canvas.clientHeight;
    const info = canvasInfoRef.current;
    const contentW = info.w > 0 ? info.w : clipW;
    const contentH = info.h > 0 ? info.h : clipH;
    const ox = info.w > 0 ? info.offsetX : 0;
    const oy = info.h > 0 ? info.offsetY : 0;

    const baseLineHeightRatio = SUBTITLE_PREVIEW_LINE_HEIGHT_RATIO;
    const baseStrokeWidthRatio = SUBTITLE_PREVIEW_STROKE_TO_FONT_FRAC;

    const vid = videoRef.current;
    const iw = vid && vid.videoWidth > 0 ? vid.videoWidth : undefined;
    const ih = vid && vid.videoHeight > 0 ? vid.videoHeight : undefined;
    const fontSize = computePreviewSubtitleDrawFontPx({
      scale: fontScale,
      contentW,
      contentH,
      intrinsicVideoWidth: iw,
      intrinsicVideoHeight: ih,
    });
    const { paddingX, paddingY } = computeSubtitleBoxPadding(fontSize);
    const lineHeight = fontSize * baseLineHeightRatio;
    const strokeWidth = Math.max(0.5, fontSize * baseStrokeWidthRatio);
    const maxWidth = contentW * widthRatio - paddingX * 2;

    ctx.font = `${fontSize}px Microsoft YaHei, Arial, sans-serif`;

    const plain = stripSubtitleLineBreakMarkers(stripTargetTextUserLineBreaks(subtitle.text));
    const lines = wrapText(plain, maxWidth, ctx, autoSubtitleLineNumberRef.current);

    const bgWidth = contentW * widthRatio;
    const bgHeight = getFixedSubtitleBoxHeight(fontSize);

    const baseX = ox + posX * contentW;
    const baseY = oy + posY * contentH;
    const x = baseX - bgWidth / 2;
    const y = baseY - bgHeight / 2;

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const textColor = '#fff';

    const startY = y + bgHeight / 2 - (lines.length - 1) * lineHeight / 2;

    lines.forEach((line, lineIndex) => {
      const lineY = startY + lineIndex * lineHeight;
      const textX = x + bgWidth / 2;

      ctx.strokeStyle = '#000000';
      ctx.lineWidth = strokeWidth;
      ctx.lineJoin = 'round';
      ctx.miterLimit = 2;
      ctx.strokeText(line, textX, lineY);

      ctx.fillStyle = textColor;
      ctx.fillText(line, textX, lineY);
    });
  }, [ctx, canvas, wrapText, getFixedSubtitleBoxHeight]);



  // 视频播放控制 - 使用useCallback优化
  const playVideo = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      video.play();
    }
  }, []);

  const stopVideo = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }, []);

  // 更新当前字幕显示 - 已合并到 renderSubtitles，此函数保留以兼容旧代码
  const updateCurrentSubtitle = useCallback(() => {
    renderSubtitles();
  }, [renderSubtitles]);

  const fetchSubtitleContentRef = useRef(fetchSubtitleContent);
  fetchSubtitleContentRef.current = fetchSubtitleContent;

  /** 仅当视频流/字幕文件 URL 等变化时重跑完整初始化；避免仅 subtitles 数组更新时反复 initVideo 重绑 HLS */
  const initVideoReloadKey = useMemo(
    () =>
      !videoData
        ? `task:${taskId ?? ''}`
        : [
            String(videoData.id ?? ''),
            String((videoData as any).pure_video_hls_url ?? ''),
            String(videoData.video_hls_url ?? ''),
            String(videoData.translated_subtitle_url ?? ''),
            String(videoData.subtitle_url ?? ''),
            String(taskId ?? ''),
          ].join('|'),
    [
      videoData?.id,
      (videoData as any)?.pure_video_hls_url,
      videoData?.video_hls_url,
      videoData?.translated_subtitle_url,
      videoData?.subtitle_url,
      taskId,
    ]
  );

  // 初始化（换源 / 换任务时执行；台词 post_text 等变化走下方 parseSubtitles effect）
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      const subtitleContent = await fetchSubtitleContentRef.current();
      if (cancelled) return;
      setFetchedSrtContent(subtitleContent);
      await initVideo();
      if (cancelled) return;
      initSubtitles();
      initCanvas();
    };
    void init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 刻意与 fetchSubtitleContent 解耦，仅用 URL 身份键触发重载
  }, [initVideoReloadKey]);

  // 监听字幕内容变化，重新解析字幕
  useEffect(() => {
    if (fetchedSrtContent || videoData?.subtitles) {
      parseSubtitles();
    }
  }, [fetchedSrtContent, videoData, targetText, parseSubtitles]);

  // 监听字幕数据变化，立即重新渲染字幕（确保译文变更后视频上的字幕同步更新）
  useEffect(() => {
    if (subtitles.length > 0 && ctx && canvas && videoRef.current) {
      // 立即触发一次字幕渲染，确保显示最新的字幕内容
      // 使用 setTimeout 确保在状态更新后执行
      const timer = setTimeout(() => {
        renderSubtitles();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [subtitles, renderSubtitles, ctx, canvas]);

  // 监听外部播放状态变化 - 使用useLayoutEffect确保在DOM更新前执行
  useLayoutEffect(() => {
    const video = videoRef.current;
    if (!video || externalIsPlaying === undefined) return;

    // 检查视频是否已加载元数据，确保readyState >= 1
    if (video.readyState < 1) {
      // 视频元数据未加载完成，等待加载
      return;
    }

    // 只有在非用户交互时才自动同步播放状态
    if (!userInteractionRef.current) {
      if (externalIsPlaying) {
        // 右侧视频静音，只播放画面
        video.muted = true;
        video.play();
        // 同步播放右侧视频（静音）
      } else {
        video.pause();
        // 同步暂停右侧视频
      }
    }
  }, [externalIsPlaying]);

  // 防抖定时器引用
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSyncTimeRef = useRef<number>(0);
  const isSyncingRef = useRef<boolean>(false);
  const userInteractionRef = useRef<boolean>(false);

  // 监听外部时间变化，实现时间同步
  useEffect(() => {
    const video = videoRef.current;
    if (!video || externalCurrentTime === undefined) return;

    // 检查视频是否已加载元数据
    if (video.readyState < 1) {
      return;
    }

    // 检查时间是否在有效范围内
    if (externalCurrentTime < 0 || (video.duration && externalCurrentTime > video.duration)) {
      return;
    }

    // 如果正在同步中，跳过本次同步
    if (isSyncingRef.current) {
      return;
    }

    // 防抖机制：避免短时间内多次同步
    const now = Date.now();
    const debounceTime = 200; // 增加防抖时间，减少同步频率
    if (now - lastSyncTimeRef.current < debounceTime) {
      return;
    }

    // 检查时间差异
    const timeDiff = Math.abs(video.currentTime - externalCurrentTime);
    const syncThreshold = 1.0; // 提高同步阈值到1秒，减少不必要的同步
    
    // 只有在时间差异很大时才进行同步，避免在正常播放过程中的瞬跳
    if (timeDiff > syncThreshold) {
      // 清除之前的定时器
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }

      // 延迟同步，给视频更多时间自然同步
      syncTimeoutRef.current = setTimeout(() => {
        if (video && !isSyncingRef.current) {
          // 再次检查时间差异，避免在延迟期间已经自然同步
          const currentTimeDiff = Math.abs(video.currentTime - externalCurrentTime);
          if (currentTimeDiff > syncThreshold) {
            isSyncingRef.current = true;
            // 同步时间
            try {
              video.currentTime = externalCurrentTime;
              lastSyncTimeRef.current = Date.now();
            } catch (error) {
              console.error('SubtitleOverlayPlayer: 时间同步失败:', error);
            } finally {
              // 同步完成后重置标记
              setTimeout(() => {
                isSyncingRef.current = false;
              }, 100); // 增加重置延迟，避免频繁同步
            }
          }
        }
      }, 100); // 增加延迟到100ms，减少同步频率
    }

    // 清理函数
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [externalCurrentTime]);

  // 监听外部时间变化，实时更新字幕显示（已优化，避免闪烁）
  useEffect(() => {
    if (externalCurrentTime !== undefined) {
      // 直接调用渲染，renderSubtitles 内部已经做了节流处理
        renderSubtitles();
    }
  }, [externalCurrentTime, renderSubtitles]);

  // 监听视频事件
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      // 视频元数据加载完成
      resizeCanvas();
      
      // 通知父组件右侧视频已就绪
      if (onRightVideoReady) {
        onRightVideoReady();
      }
      
      // 元数据加载完成后，检查是否需要立即响应外部播放状态
      if (externalIsPlaying !== undefined && !userInteractionRef.current) {
        if (externalIsPlaying) {
          video.muted = true;
          video.play();
          // 元数据加载完成，立即同步播放右侧视频（静音）
        } else {
          video.pause();
          // 元数据加载完成，立即同步暂停右侧视频
        }
      }
    };

    const handleTimeUpdate = () => {
      // 视频自身的时间更新，直接渲染字幕（内部已做节流）
      renderSubtitles();
    };

    const handleError = (e: Event) => {
      console.error('视频加载失败:', e);
    };

    // 使用 ResizeObserver 监听 video：canvas 尺寸由 resizeCanvas 跟随 video 同步，勿再 observe canvas，否则会与 style 更新形成双触发、加剧闪屏
    const setupResizeObserver = () => {
      if (!video) return;

      // 清理之前的观察者
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }

      // 创建新的 ResizeObserver；回调在 layout 后、paint 前，合并到下一帧 rAF 再量尺寸，减少与 paint 的竞态
      resizeObserverRef.current = new ResizeObserver(() => {
        if (resizeObserverCoalesceRafRef.current !== null) {
          cancelAnimationFrame(resizeObserverCoalesceRafRef.current);
        }
        resizeObserverCoalesceRafRef.current = requestAnimationFrame(() => {
          resizeObserverCoalesceRafRef.current = null;
          resizeCanvas();
        });
      });

      resizeObserverRef.current.observe(video);
    };
    
    setupResizeObserver();

    const handlePlay = () => {
      // 只有在用户主动操作右侧视频时才触发外部同步
      // 避免在自动同步时触发循环
      if (onExternalPlay && !isSyncingRef.current && userInteractionRef.current) {
        // 用户操作右侧视频播放，同步左侧视频
        onExternalPlay();
      }
      // 重置用户交互标记
      userInteractionRef.current = false;
    };

    const handlePause = () => {
      // 只有在用户主动操作右侧视频时才触发外部同步
      // 避免在自动同步时触发循环
      if (onExternalPause && !isSyncingRef.current && userInteractionRef.current) {
        // 用户操作右侧视频暂停，同步左侧视频
        onExternalPause();
      }
      // 重置用户交互标记
      userInteractionRef.current = false;
    };

    const handleSeeked = () => {
      // 只有在用户主动操作右侧视频时才触发外部同步
      // 避免在自动同步时触发循环
      if (onExternalSeek && !video.seeking) {
        const timeDiff = Math.abs(video.currentTime - (externalCurrentTime || 0));
        if (timeDiff > 0.5) { // 只有差异较大时才同步
          // 用户操作右侧视频，同步左侧视频
          onExternalSeek(video.currentTime);
        }
      }
    };

    // 用户交互检测
    const handleUserInteraction = () => {
      userInteractionRef.current = true;
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('error', handleError);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('seeked', handleSeeked);

    /**
     * 直接打开编辑页 / 监听器因依赖变化被卸载重绑时，视频可能已 HAVE_METADATA，
     * loadedmetadata 不再触发，会导致 onRightVideoReady 永不回调、左侧 init 死锁。
     */
    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      queueMicrotask(() => handleLoadedMetadata());
    }
    
    // 监听用户交互事件
    video.addEventListener('click', handleUserInteraction);
    video.addEventListener('touchstart', handleUserInteraction);
    video.addEventListener('keydown', handleUserInteraction);
    
    // 监听窗口大小变化（作为 ResizeObserver 的补充）
    const handleWindowResize = () => {
        resizeCanvas();
    };
    
    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('orientationchange', handleWindowResize);
    
    // 页面可见性变化时重新计算
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // 页面重新可见时，立即更新
        resizeCanvas();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('error', handleError);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('click', handleUserInteraction);
      video.removeEventListener('touchstart', handleUserInteraction);
      video.removeEventListener('keydown', handleUserInteraction);
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('orientationchange', handleWindowResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // 清理 ResizeObserver
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (resizeObserverCoalesceRafRef.current !== null) {
        cancelAnimationFrame(resizeObserverCoalesceRafRef.current);
        resizeObserverCoalesceRafRef.current = null;
      }
      
      // 取消未完成的动画帧请求
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      
      // 取消未完成的渲染请求
      if (renderRafIdRef.current !== null) {
        cancelAnimationFrame(renderRafIdRef.current);
        renderRafIdRef.current = null;
      }
    };
  }, [resizeCanvas, updateCurrentSubtitle]);

  /** 与 onResize 内 clamp 一致；拖拽中 Resizable 忽略受控 size，必须把像素上下限交给库，否则会出现拖时右边「涨出去」、松手才收回 */
  const clipInfoForRnd = canvasInfoRef.current;
  const rndWMax =
    clipInfoForRnd.w > 0 ? clipInfoForRnd.w * SUBTITLE_BOX_WIDTH_RATIO_MAX : Number.MAX_SAFE_INTEGER;
  // 使用 ref 而非 state（rndContentMinWidth）：ref 在 recomputeContentMinBoxWidth 中同步更新，
  // 避免 React 批处理导致 Rnd minWidth prop 滞后于实际最小宽度
  const rndGlobalWMin =
    clipInfoForRnd.w > 0
      ? Math.max(40, Math.round(clipInfoForRnd.w * SUBTITLE_BOX_WIDTH_RATIO_MIN))
      : 40;
  const rndWMin =
    typeof rndCornerLockAspect === 'number'
      ? rndGlobalWMin
      : Math.max(rndGlobalWMin, contentMinBoxWidthPxRef.current);
  const rndHMax =
    clipInfoForRnd.h > 0 ? clipInfoForRnd.h : Number.MAX_SAFE_INTEGER;
  const rndHMin = (() => {
    if (clipInfoForRnd.h <= 0 || clipInfoForRnd.w <= 0) return 20;
    const intrinsic = getIntrinsicVideoSize();
    const minH = computeFixedSubtitleBoxHeightFromScale({
      scale: SUBTITLE_PREVIEW_SCALE_MIN,
      lineCount: autoSubtitleLineNumberRef.current,
      contentW: clipInfoForRnd.w,
      contentH: clipInfoForRnd.h,
      verticalPaddingPx: SUBTITLE_VERTICAL_PADDING,
      intrinsicVideoWidth: intrinsic.width,
      intrinsicVideoHeight: intrinsic.height,
    });
    return Math.max(20, Math.round(minH));
  })();

  return (
    <div className={`subtitle-overlay-player-container ${className}`}>
      <div className="video-container">
        <video ref={videoRef}>
          <track ref={trackRef} kind="subtitles" srcLang="zh" label="中文字幕" />
          您的浏览器不支持视频播放。
        </video>
        {/* 与视频对齐的裁剪容器，约束字幕框不超出画面 */}
        <div ref={videoClipRef} className="subtitle-video-clip" style={{ position: 'absolute', overflow: 'hidden' }}>
          <canvas
            ref={canvasRef}
            className="canvas-subtitle-overlay"
            onClick={handleCanvasClick}
          />
          {interactive && subtitleSelected && rndState.w > 0 && rndState.h > 0 && (
            <Rnd
              className="subtitle-rnd-overlay"
              size={{ width: rndState.w, height: rndState.h }}
              position={{ x: rndState.x, y: rndState.y }}
              bounds="parent"
              minWidth={rndWMin}
              maxWidth={rndWMax}
              minHeight={rndHMin}
              maxHeight={rndHMax}
              lockAspectRatio={typeof rndCornerLockAspect === 'number' ? rndCornerLockAspect : false}
              dragAxis="both"
              enableResizing={SUBTITLE_RND_RESIZE_ENABLE}
              resizeHandleStyles={SUBTITLE_RND_RESIZE_HANDLE_STYLES}
              onDragStart={() => { isDraggingRef.current = true; }}
              onDrag={(_e, d) => {
                const info = canvasInfoRef.current;
                if (info.w <= 0 || info.h <= 0) return;
                const curW = rndState.w;
                const curH = rndState.h;
                const newCenterX = d.x + curW / 2;
                const newCenterY = d.y + curH / 2;
                const nx = info.w > 0 ? Math.max(0, Math.min(1, (newCenterX - info.offsetX) / info.w)) : 0.5;
                const ny = info.h > 0 ? Math.max(0, Math.min(1, (newCenterY - info.offsetY) / info.h)) : 0.85;
                subtitlePosRef.current = { x: nx, y: ny };
                renderSubtitlesRef.current?.();
                setRndState(prev => ({ ...prev, x: d.x, y: d.y }));
                onSubtitlePositionChange?.({ x: nx, y: ny });
              }}
              onDragStop={() => {
                isDraggingRef.current = false;
                const info = canvasInfoRef.current;
                const video = videoRef.current;
                const taskIdNum = videoData?.id ?? (taskId ? parseInt(String(taskId), 10) : NaN);
                if (!video || !Number.isFinite(taskIdNum)) return;
                const payload = buildGenerateVideoPayloadRef.current(taskIdNum);
                if (payload) console.log('[字幕框-位置]', payload);
              }}
              onResizeStart={(_e, dir, _el) => {
                isDraggingRef.current = true;
                const d = dir as ResizeDirection;
                const info = canvasInfoRef.current;
                if (isSubtitleResizeCorner(d) && info.w > 0 && info.h > 0) {
                  const { boxW, boxH } = getModelSubtitleBoxSize(info.w, info.h);
                  const ar = computeSubtitleCornerLockAspect(boxW, boxH);
                  if (ar != null) {
                    subtitleCornerResizeAspectRef.current = ar;
                    flushSync(() => {
                      setRndCornerLockAspect(ar);
                    });
                  } else {
                    subtitleCornerResizeAspectRef.current = null;
                    flushSync(() => {
                      setRndCornerLockAspect(false);
                    });
                  }
                } else {
                  subtitleCornerResizeAspectRef.current = null;
                  flushSync(() => {
                    setRndCornerLockAspect(false);
                  });
                }
              }}
              onResize={(_e, dir, refEl, _delta, position) => {
                const info = canvasInfoRef.current;
                if (info.w <= 0 || info.h <= 0) return;
                const rawW = parseFloat(refEl.style.width);
                const rawH = parseFloat(refEl.style.height);
                const d = dir as ResizeDirection;

                let nextW = rawW;
                let nextH = rawH;
                if (isSubtitleResizeEastWest(d)) {
                  const clampedW = clampSubtitleBoxWidthPx(
                    rawW,
                    info.w,
                    contentMinBoxWidthPxRef.current
                  );
                  subtitleWidthRatioRef.current = clampSubtitleBoxWidthRatio(clampedW / info.w);
                  nextW = info.w * subtitleWidthRatioRef.current;
                  const fontSize = getSubtitleFontSize(info.w, info.h);
                  nextH = getFixedSubtitleBoxHeight(fontSize);
                } else if (isSubtitleResizeCorner(d)) {
                  let synced = applyCornerResizeFromWidth(rawW, info.w, info.h);
                  if (subtitleWrapExceedsLineCap(synced.boxW, info.w, info.h)) {
                    recomputeContentMinBoxWidth();
                    synced = applyCornerResizeFromWidth(
                      rawW,
                      info.w,
                      info.h,
                      contentMinBoxWidthPxRef.current
                    );
                  }
                  nextW = synced.boxW;
                  nextH = synced.boxH;
                }

                const nextX = d === 'left' || d === 'topLeft' || d === 'bottomLeft'
                  ? position.x + rawW - nextW
                  : position.x;
                const nextY = position.y + rawH / 2 - nextH / 2;
                const newCenterX = nextX + nextW / 2;
                const newCenterY = nextY + nextH / 2;
                const nx = info.w > 0 ? Math.max(0, Math.min(1, (newCenterX - info.offsetX) / info.w)) : 0.5;
                const ny = info.h > 0 ? Math.max(0, Math.min(1, (newCenterY - info.offsetY) / info.h)) : 0.85;
                subtitlePosRef.current = { x: nx, y: ny };
                refEl.style.width = `${nextW}px`;
                refEl.style.height = `${nextH}px`;

                renderSubtitlesRef.current?.({ force: true });
                setRndState({ x: nextX, y: nextY, w: nextW, h: nextH });
                rndSizeRef.current = { w: nextW, h: nextH };
                onSubtitlePositionChange?.({ x: nx, y: ny });
                onSubtitleScaleChange?.(subtitleScaleRef.current);
                onSubtitleWidthRatioChange?.(subtitleWidthRatioRef.current);
              }}
              onResizeStop={(_e, dir, refEl, _delta, position) => {
                const info = canvasInfoRef.current;
                const d = dir as ResizeDirection;
                if (refEl && info.w > 0 && info.h > 0) {
                  // 使用 style 获取精确值，与 onResize 保持一致（offsetWidth 是整数且可能含 border/padding）
                  const rawW = parseFloat(refEl.style.width);
                  const rawH = parseFloat(refEl.style.height);
                  if (rawW > 0 && rawH > 0) {
                    let nextW = rawW;
                    let nextH = rawH;
                    if (isSubtitleResizeEastWest(d)) {
                      const clampedW = clampSubtitleBoxWidthPx(
                        rawW,
                        info.w,
                        contentMinBoxWidthPxRef.current
                      );
                      subtitleWidthRatioRef.current = clampSubtitleBoxWidthRatio(clampedW / info.w);
                      nextW = info.w * subtitleWidthRatioRef.current;
                      const fontSize = getSubtitleFontSize(info.w, info.h);
                      nextH = getFixedSubtitleBoxHeight(fontSize);
                    } else if (isSubtitleResizeCorner(d)) {
                      let synced = applyCornerResizeFromWidth(rawW, info.w, info.h);
                      if (subtitleWrapExceedsLineCap(synced.boxW, info.w, info.h)) {
                        recomputeContentMinBoxWidth();
                        synced = applyCornerResizeFromWidth(
                          rawW,
                          info.w,
                          info.h,
                          contentMinBoxWidthPxRef.current
                        );
                      }
                      nextW = synced.boxW;
                      nextH = synced.boxH;
                    }
                    const nextX = d === 'left' || d === 'topLeft' || d === 'bottomLeft'
                      ? position.x + rawW - nextW
                      : position.x;
                    const nextY = position.y + rawH / 2 - nextH / 2;
                    const nx =
                      info.w > 0
                        ? Math.max(0, Math.min(1, (nextX + nextW / 2 - info.offsetX) / info.w))
                        : 0.5;
                    const ny =
                      info.h > 0
                        ? Math.max(0, Math.min(1, (nextY + nextH / 2 - info.offsetY) / info.h))
                        : 0.85;
                    subtitlePosRef.current = { x: nx, y: ny };
                    setRndState({ x: nextX, y: nextY, w: nextW, h: nextH });
                    rndSizeRef.current = { w: nextW, h: nextH };
                    onSubtitlePositionChange?.({ x: nx, y: ny });
                    onSubtitleScaleChange?.(subtitleScaleRef.current);
                    onSubtitleWidthRatioChange?.(subtitleWidthRatioRef.current);
                    renderSubtitlesRef.current?.({ force: true });
                  }
                }
                isDraggingRef.current = false;
                subtitleCornerResizeAspectRef.current = null;
                contentMinWidthCacheKeyRef.current = '';
                recomputeContentMinBoxWidth();
                if (isSubtitleResizeEastWest(d) || isSubtitleResizeCorner(d)) {
                  syncActiveSubtitlePostTextFromBox();
                }
                // 延迟解除比例锁定，避免 react-rnd 立即重新渲染导致尺寸跳变
                requestAnimationFrame(() => {
                  setRndCornerLockAspect(false);
                });
                const video = videoRef.current;
                const taskIdNum = videoData?.id ?? (taskId ? parseInt(String(taskId), 10) : NaN);
                if (!video || !Number.isFinite(taskIdNum)) return;
                const payload = buildGenerateVideoPayloadRef.current(taskIdNum);
                if (payload) console.log('[字幕框]', payload);
              }}
            >
              <div
                className="subtitle-rnd-line-badge"
                role="group"
                aria-label={`字幕行数 ${displaySubtitleLineNumber} 行`}
              >
                <button
                  type="button"
                  className="subtitle-rnd-line-badge__btn"
                  aria-label="减少行数"
                  disabled={
                    lineAdjusting || displaySubtitleLineNumber <= SUBTITLE_LINE_NUMBER_MIN
                  }
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => void handleLineNumberAdjust(-1, e)}
                >
                  ▼
                </button>
                <span className="subtitle-rnd-line-badge__label">{displaySubtitleLineNumber} 行</span>
                <button
                  type="button"
                  className="subtitle-rnd-line-badge__btn"
                  aria-label="增加行数"
                  disabled={
                    lineAdjusting || displaySubtitleLineNumber >= SUBTITLE_LINE_NUMBER_ADJUST_MAX
                  }
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => void handleLineNumberAdjust(1, e)}
                >
                  ▲
                </button>
              </div>
            </Rnd>
          )}
        </div>
      </div>
      
      {/* {showControls && (
        <div className="controls">
          <div className="video-controls">
            <h4>🎬 视频播放控制</h4>
            <button onClick={playVideo}>▶️ 播放</button>
            <button onClick={stopVideo}>⏹️ 停止</button>
          </div>
        </div>
      )} */}
    </div>
  );
});

SubtitleOverlayPlayer.displayName = 'SubtitleOverlayPlayer';

export default SubtitleOverlayPlayer;
